import { useTranslations } from "next-intl";
import PropTypes from 'prop-types';
import Input from "../form-fields/Input";
import Textarea from "../form-fields/Textarea";
import SearchableSelect from "../form-fields/SearchableSelect";
import RadioInput from "../form-fields/RadioInput";

export default function CustomerForm({
  formData,
  handleChange,
  handleCityChange,
  states,
  countries,
  citiesList,
  streetsList,
  errors = {},
  customerType,
  handleCustomerTypeChange,
  addressType,
  handleAddressTypeChange,
  prefix = "",
  showCustomerType = true,
  title = "",
  userData,
}) {
  const t = useTranslations("Account");

  const getFieldName = (field) => prefix ? `${prefix}.${field}` : field;

  return (
    <>
      <div className="col-span-2 sm:col-span-1">
        <Input
          title={t("first_name")}
          type="text"
          name={getFieldName("first_name")}
          value={prefix ? formData[prefix]?.first_name : formData.first_name}
          handleChange={handleChange}
          required
          error={errors[prefix]?.first_name}
        />
      </div>
      <div className="col-span-2 sm:col-span-1">
        <Input
          title={t("last_name")}
          type="text"
          name={getFieldName("last_name")}
          value={prefix ? formData[prefix]?.last_name : formData.last_name}
          handleChange={handleChange}
          required
          error={errors[prefix]?.last_name}
        />
      </div>
      <div className={prefix === "billing" ? "col-span-2 sm:col-span-1" : "col-span-2"}>
        <Input
          title={t("phone")}
          type="tel"
          name={getFieldName("phone")}
          value={prefix ? formData[prefix]?.phone : formData.phone}
          handleChange={handleChange}
          required
          error={errors[prefix]?.phone}
          className="rtl:text-right"
        />
      </div>
      {prefix === "billing" && (
        <>
          <div className="col-span-2 sm:col-span-1">
            <Input
              title={t("email")}
              type="email"
              name={getFieldName("email")}
              value={prefix ? formData[prefix]?.email : formData.email}
              handleChange={handleChange}
              required
              error={errors[prefix]?.email}
            />
          </div>
          {(userData?.isGuest || !localStorage.getItem('token')) && (
            <>
              <div className="col-span-2 sm:col-span-1">
                <Input
                  title={t("password")}
                  type="password"
                  name={getFieldName("password")}
                  value={prefix ? formData[prefix]?.password : formData.password}
                  handleChange={handleChange}
                  required
                  error={errors[prefix]?.password}
                  minLength={8}
                  data-testid="customer-password-input"
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Input
                  title={t("repeat_password")}
                  type="password"
                  name={getFieldName("repeat_password")}
                  value={prefix ? formData[prefix]?.repeat_password : formData.repeat_password}
                  handleChange={handleChange}
                  required
                  error={errors[prefix]?.repeat_password}
                  minLength={8}
                  data-testid="customer-repeat-password-input"
                />
              </div>
            </>
          )}
        </>
      )}

      {showCustomerType && (
        <>
          <div className="col-span-1">
            <RadioInput
              name="customer_type"
              value="personal"
              checked={customerType === "personal"}
              onChange={handleCustomerTypeChange}
              label={t("personal")}
            />
          </div>
          <div className="col-span-1">
            <RadioInput
              name="customer_type"
              value="company"
              checked={customerType === "company"}
              onChange={handleCustomerTypeChange}
              label={t("company")}
            />
          </div>
          {customerType === "company" && (
            <>
              <div className="col-span-2 sm:col-span-1">
                <Input
                  title={t("company_name")}
                  type="text"
                  name={getFieldName("company")}
                  value={prefix ? formData[prefix]?.company : formData.company}
                  handleChange={handleChange}
                  required
                  error={errors[prefix]?.company}
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Input
                  title={t("company_id")}
                  type="text"
                  name={getFieldName("company_id")}
                  value={prefix ? formData[prefix]?.company_id : formData.company_id}
                  handleChange={handleChange}
                  required
                  error={errors[prefix]?.company_id}
                />
              </div>
            </>
          )}
        </>
      )}
      <div className="col-span-2">
        <h2 className="mb-4 text-2xl font-bold text-primary-studio font-secondary">{title}</h2>
        <hr className="dotted-spaced my-4" />
      </div>

      <div className="col-span-1">
        <RadioInput
          name={getFieldName("address_type")}
          value="apartment"
          checked={addressType === "apartment"}
          onChange={handleAddressTypeChange}
          label={t("apartment")}
        />
      </div>
      <div className="col-span-1">
        <RadioInput
          name={getFieldName("address_type")}
          value="house"
          checked={addressType === "house"}
          onChange={handleAddressTypeChange}
          label={t("house")}
        />
      </div>
      <div className="col-span-2">
        {citiesList && citiesList.length === 1 ? (
          <Input
            title={t("city")}
            type="text"
            name={getFieldName("city")}
            value={prefix ? formData[prefix]?.city : formData.city}
            handleChange={handleCityChange}
            required
            error={errors[prefix]?.city}
          />
        ) : (
          <SearchableSelect
            title={t("city")}
            name={getFieldName("city")}
            value={prefix ? formData[prefix]?.city : formData.city}
            options={citiesList}
            handleChange={handleCityChange}
            placeholder={t("select_city")}
            required
            error={errors[prefix]?.city}
          />
        )}
      </div>
      <div className="col-span-2 sm:col-span-1">
        {streetsList && streetsList.length === 1 ? (
          <Input
            title={t("street")}
            type="text"
            name={getFieldName("street")}
            value={prefix ? formData[prefix]?.street : formData.street}
            handleChange={handleChange}
            required
            error={errors[prefix]?.street}
          />
        ) : (
          <SearchableSelect
            title={t("street")}
            name={getFieldName("street")}
            value={prefix ? formData[prefix]?.street : formData.street}
            options={streetsList}
            handleChange={handleChange}
            placeholder={t("select_street")}
            required
            error={errors[prefix]?.street}
          />
        )}
      </div>
      <div className="col-span-1">
        <Input
          title={t("house_number")}
          type="text"
          name={getFieldName("house_number")}
          value={prefix ? formData[prefix]?.house_number : formData.house_number}
          handleChange={handleChange}
          required
          error={errors[prefix]?.house_number}
        />
      </div>
      {addressType === "apartment" && (
        <>
          <div className="col-span-1">
            <Input
              title={t("floor")}
              type="text"
              name={getFieldName("floor")}
              value={prefix ? formData[prefix]?.floor : formData.floor}
              handleChange={handleChange}
              required
              error={errors[prefix]?.floor}
            />
          </div>
          <div className="col-span-1">
            <Input
              title={t("apartment_number")}
              type="text"
              name={getFieldName("apartment_number")}
              value={prefix ? formData[prefix]?.apartment_number : formData.apartment_number}
              handleChange={handleChange}
              required
              error={errors[prefix]?.apartment_number}
            />
          </div>
        </>
      )}
      <div className="col-span-2">
        <Textarea
          title={t("address_notes")}
          name={getFieldName("address_notes")}
          value={prefix ? formData[prefix]?.address_notes : formData.address_notes}
          handleChange={handleChange}
          error={errors[prefix]?.address_notes}
          rows={3}
        />
      </div>
      <div className="col-span-2">
        {Object.keys(countries).length === 1 ? (
          <input type="hidden" name="billing.country" value={formData.billing.country} onChange={handleChange} />
        ) : (
          <SearchableSelect
            title={t("country")}
            name={getFieldName("country")}
            value={prefix ? formData[prefix]?.country : formData.country}
            options={Object.entries(countries).map(([code, name]) => ({ id: code, name }))}
            handleChange={handleChange}
            placeholder={t("select_country")}
            required
            error={errors[prefix]?.country}
          />
        )}
      </div>
      {Object.keys(states).length > 0 && (
        <div className="col-span-1">
          <SearchableSelect
            title={t("state")}
            name={getFieldName("state")}
            value={prefix ? formData[prefix]?.state : formData.state}
            options={Object.entries(states).map(([code, name]) => ({ id: code, name }))}
            handleChange={handleChange}
            placeholder={t("select_state")}
            required
            error={errors[prefix]?.state}
          />
        </div>
      )}
    </>
  );
}

CustomerForm.propTypes = {
  formData: PropTypes.oneOfType([
    PropTypes.shape({
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      email: PropTypes.string,
      phone: PropTypes.string,
      company: PropTypes.string,
      company_id: PropTypes.string,
      country: PropTypes.string,
      state: PropTypes.string,
      city: PropTypes.string,
      street: PropTypes.string,
      house_number: PropTypes.string,
      floor: PropTypes.string,
      apartment_number: PropTypes.string,
      password: PropTypes.string,
      repeat_password: PropTypes.string,
      isGuest: PropTypes.bool,
      address_notes: PropTypes.string,
    }),
    PropTypes.shape({
      billing: PropTypes.shape({
        first_name: PropTypes.string,
        last_name: PropTypes.string,
        email: PropTypes.string,
        phone: PropTypes.string,
        company: PropTypes.string,
        company_id: PropTypes.string,
        country: PropTypes.string,
        state: PropTypes.string,
        city: PropTypes.string,
        street: PropTypes.string,
        house_number: PropTypes.string,
        floor: PropTypes.string,
        apartment_number: PropTypes.string,
        address_notes: PropTypes.string,
      }),
      shipping: PropTypes.shape({
        first_name: PropTypes.string,
        last_name: PropTypes.string,
        email: PropTypes.string,
        phone: PropTypes.string,
        country: PropTypes.string,
        state: PropTypes.string,
        city: PropTypes.string,
        street: PropTypes.string,
        house_number: PropTypes.string,
        floor: PropTypes.string,
        apartment_number: PropTypes.string,
        address_notes: PropTypes.string,
      }),
    }),
  ]).isRequired,
  handleChange: PropTypes.func.isRequired,
  handleCityChange: PropTypes.func.isRequired,
  states: PropTypes.object.isRequired,
  countries: PropTypes.object.isRequired,
  citiesList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  })),
  streetsList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  })),
  errors: PropTypes.object,
  customerType: PropTypes.string.isRequired,
  handleCustomerTypeChange: PropTypes.func.isRequired,
  addressType: PropTypes.string.isRequired,
  handleAddressTypeChange: PropTypes.func.isRequired,
  prefix: PropTypes.string,
  showCustomerType: PropTypes.bool,
  title: PropTypes.string,
  userData: PropTypes.shape({
    isGuest: PropTypes.bool,
  }),
};